import { useState } from 'react';
import type { AnyEntity, SyncConflict } from '@/domain/schema/v1';
import { changedContentFields, isFrozen } from '@/domain/rules/writeRules';
import { openConflicts, resolve } from '@/services/syncService';
import { useLoad } from '../hooks/useLoad';
import { useRequiredSession } from '../state/session';
import { useSyncUi } from '../state/sync';
import { ConfirmButton } from './fields';

const TYPE_LABEL: Record<string, string> = {
  Client: '내담자',
  Session: '회기',
  CounselingJournal: '상담기록지',
  CaseFormulationRevision: '사례개념화',
  DiagnosticConsideration: '진단적 고려',
  CounselingGoal: '상담목표',
  GoalProgress: '목표 진행',
  RiskAssessment: '위험도',
  SessionAnalysis: '회기 분석',
  NextSessionBrief: '다음 회기 준비',
  CounselorMemo: '상담자 메모',
  DocumentArtifact: '문서',
  IntakeSummary: '초기 상담 요약',
  ClientIdentity: '민감정보',
};

const REASON: Record<string, string> = {
  'same-field': '두 기기에서 같은 항목을 고쳤습니다',
  'approved-record': '한쪽에서 승인된 기록입니다 (승인된 쪽만 유지할 수 있습니다)',
  'no-common-base': '두 버전의 공통 출발점을 찾을 수 없습니다',
  'invalid-change': '허용되지 않는 변경이 들어왔습니다',
};

/** 설정 > 동기화 (UX_FLOW §17) */
export function SyncSettings() {
  const session = useRequiredSession();
  const ui = useSyncUi();
  const { data: conflicts, reload } = useLoad(() => openConflicts(session), [session, ui.lastSyncAt, ui.conflicts]);

  return (
    <div className="card">
      <h2>Google Drive 동기화</h2>
      <p className="muted small">
        이 기기에서 암호화한 뒤 Google Drive의 앱 전용 숨김 공간에 저장합니다. 일반 Drive 파일에는 접근하지 않으며,
        Drive에서는 내용을 볼 수 없습니다.
      </p>
      {!ui.connected ? (
        <button type="button" className="primary" onClick={() => void ui.connect(session)}>
          Google Drive 연결
        </button>
      ) : (
        <>
          <p>
            상태:{' '}
            {ui.running
              ? '동기화 중…'
              : ui.tokenValid
                ? ui.stayConnected
                  ? '연결됨 (자동 유지)'
                  : '연결됨'
                : '연결 만료 — 다시 연결 필요'}
            {ui.lastSyncAt && ` · 마지막 동기화 ${new Date(ui.lastSyncAt).toLocaleString('ko-KR')}`}
            {ui.pending > 0 && ` · 올릴 변경 ${ui.pending}건`}
          </p>
          <div className="row">
            {ui.tokenValid ? (
              <button type="button" className="primary" disabled={ui.running} onClick={() => void ui.syncNow(session)}>
                지금 동기화
              </button>
            ) : (
              <button type="button" className="primary" onClick={() => void ui.connect(session)}>
                다시 연결
              </button>
            )}
            <ConfirmButton
              label="연결 해제"
              confirmText="이 기기의 동기화를 멈춥니다. 데이터는 이 기기와 Drive에 그대로 남습니다."
              onConfirm={() => void ui.disconnect(session)}
            />
          </div>
        </>
      )}
      {ui.notice && <p className="warn small">{ui.notice}</p>}
      {ui.error && (
        <p role="alert" className="warn">
          {ui.error}
        </p>
      )}
      {conflicts && conflicts.length > 0 && (
        <div className="stack">
          <h2>동기화 충돌 {conflicts.length}건</h2>
          <p className="muted small">
            자동으로 덮어쓰지 않았습니다. 이 기기의 내용은 그대로이며, 어느 쪽을 남길지 골라 주세요.
          </p>
          {conflicts.map((c) => (
            <ConflictCard key={c.id} c={c} onDone={() => (reload(), void ui.refresh(session))} />
          ))}
        </div>
      )}
    </div>
  );
}

function show(v: unknown): string {
  if (v === undefined || v === null || v === '') return '(비어 있음)';
  if (typeof v === 'string') return v.length > 300 ? `${v.slice(0, 300)}…` : v;
  return JSON.stringify(v).slice(0, 300);
}

function ConflictCard({ c, onDone }: { c: SyncConflict; onDone: () => void }) {
  const session = useRequiredSession();
  const [err, setErr] = useState<string | null>(null);
  const local = c.localPayload as AnyEntity;
  const remote = c.remotePayload as AnyEntity;
  const fields = changedContentFields(local, remote).filter((f) => f !== 'fieldSources');
  const localFrozen = isFrozen(local);
  const remoteFrozen = isFrozen(remote);
  const pick = (choice: 'local' | 'remote') =>
    void resolve(session, c.id, choice)
      .then(onDone)
      .catch((e: Error) => setErr(e.message));
  return (
    <div className="item-block stack">
      <strong>{TYPE_LABEL[c.targetEntityType] ?? c.targetEntityType}</strong>
      <span className="muted small">{REASON[c.reason ?? ''] ?? ''}</span>
      <table className="hyp-table">
        <thead>
          <tr>
            <th>항목</th>
            <th>이 기기</th>
            <th>다른 기기</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f}>
              <td>{f}</td>
              <td>{show((local as Record<string, unknown>)[f])}</td>
              <td>{show((remote as Record<string, unknown>)[f])}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {err && <p className="warn small">{err}</p>}
      <div className="row">
        <button type="button" disabled={remoteFrozen && !localFrozen} onClick={() => pick('local')}>
          이 기기 내용 유지
        </button>
        <button type="button" disabled={localFrozen} onClick={() => pick('remote')}>
          다른 기기 내용으로
        </button>
      </div>
    </div>
  );
}
